import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { combineLatest, distinctUntilChanged, finalize, map, Subscription } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import {
  DataViewShellComponent,
  ErrorBlockComponent,
  PageContextGuardComponent,
  PageShellComponent,
} from '@clinic-platform/ui-components';

@Component({
  selector: 'app-security-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    PageHeaderComponent,
    TranslatePipe,
    CompanyContextRequiredComponent,
    DataViewShellComponent,
    PageShellComponent,
    ErrorBlockComponent,
    PageContextGuardComponent,
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        [title]="'security.page.title' | t"
        [subtitle]="'security.page.subtitle' | t"
      />

    <section class="security-card">
      <h3>{{ 'security.2fa.title' | t }}</h3>

      @if (isLoadingStatus) {
        <div class="security-meta">{{ 'security.loading' | t }}</div>
      } @else {
        <div class="security-meta">
          {{ 'security.required' | t }}: <strong>{{ twoFactorRequired ? ('common.yes' | t) : ('common.no' | t) }}</strong>
          ·
          {{ 'security.enabled' | t }}: <strong>{{ twoFactorEnabled ? ('common.yes' | t) : ('common.no' | t) }}</strong>
        </div>
      }

      @if (actionErrorText) {
        <lib-error-block variant="error" [message]="(actionErrorText | t) ?? ''" />
      }
      @if (successText) {
        <lib-error-block variant="success" [message]="(successText | t) ?? ''" />
      }

      @if (!twoFactorEnabled) {
        <button type="button" class="security-action" (click)="startSetup()" [disabled]="isBusy">
          {{ isBusy ? ('security.preparing' | t) : ('security.setup' | t) }}
        </button>
      }

      @if (setupData) {
        <div class="security-setup">
          <h4>{{ 'security.scanAndConfirm' | t }}</h4>
          <img [src]="setupData.qrImageUrl" [alt]="'security.qrAlt' | t" class="security-qr" />
          <div class="security-secret">{{ 'security.secret' | t }}: <code>{{ setupData.secret }}</code></div>
          <div class="security-uri">{{ 'security.otpUri' | t }}: <code>{{ setupData.otpauthUrl }}</code></div>

          <form [formGroup]="enableForm" (ngSubmit)="confirmEnable()">
            <label>
              {{ 'security.code' | t }}
              <input type="text" formControlName="code" maxlength="32" />
            </label>
            <button type="submit" class="security-action" [disabled]="isBusy || enableForm.invalid">
              {{ isBusy ? ('security.verifying' | t) : ('security.enable' | t) }}
            </button>
          </form>
        </div>
      }

      @if (recoveryCodes.length > 0) {
        <div class="security-recovery">
          <h4>{{ 'security.recoveryCodes' | t }}</h4>
          <p>{{ 'security.recoveryHint' | t }}</p>
          <ul>
            @for (code of recoveryCodes; track code) {
              <li><code>{{ code }}</code></li>
            }
          </ul>
        </div>
      }

      @if (twoFactorEnabled && !twoFactorRequired) {
        <form [formGroup]="disableForm" (ngSubmit)="disableTwoFactor()" class="security-disable">
          <label>
            {{ 'security.disableCode' | t }}
            <input type="text" formControlName="code" maxlength="32" />
          </label>
          <button type="submit" class="security-action security-action--danger" [disabled]="isBusy || disableForm.invalid">
            {{ isBusy ? ('security.disabling' | t) : ('security.disable' | t) }}
          </button>
        </form>
      } @else if (twoFactorRequired && twoFactorEnabled) {
        <div class="security-meta">{{ 'security.mandatoryNoDisable' | t }}</div>
      }
    </section>

    @if (canManagePolicy) {
      <section class="security-card">
        <h3>{{ policySectionTitle | t }}</h3>
        <div class="security-meta">{{ policySectionDescription | t }}</div>
        @if (isSystemAdminActor) {
          <div class="security-meta">
            Scope from header:
            <strong>{{ isGlobalScope ? 'Global' : 'Company' }}</strong>
          </div>
          <div class="security-meta">
            Selected company: <strong>{{ contextCompanyId || 'not selected' }}</strong>
          </div>
        }

        @if (policyErrorText) {
          <lib-error-block variant="error" [message]="(policyErrorText | t) ?? ''" />
        }
        @if (policySuccessText) {
          <lib-error-block variant="success" [message]="(policySuccessText | t) ?? ''" />
        }
        @if (!isCompanyScopeWithoutCompany && policyScope === 'company') {
          <div class="security-meta">
            {{ 'security.policy.source' | t }}:
            <strong>{{ policyOverrideEnabled ? ('security.policy.source.override' | t) : ('security.policy.source.inherited' | t) }}</strong>
          </div>
        }

        <lib-page-context-guard [blocked]="isCompanyScopeWithoutCompany">
          <app-company-context-required />
        </lib-page-context-guard>

        @if (!isCompanyScopeWithoutCompany) {
          <lib-data-view-shell
            [isLoading]="isPolicyLoading"
            [hasError]="false"
            [isEmpty]="!isPolicyLoading && editableMandatoryRoles.length === 0"
          >
            <div dataViewLoading class="security-meta">{{ 'security.policy.loading' | t }}</div>

            <div dataViewEmpty class="security-meta">{{ 'security.policy.noEditableRoles' | t }}</div>

            <div class="security-roles-grid">
              @for (role of editableMandatoryRoles; track role) {
                <label class="security-role-toggle">
                  <input
                    type="checkbox"
                    [checked]="isRoleMandatory(role)"
                    (change)="toggleMandatoryRole(role, $any($event.target).checked)"
                    [disabled]="isPolicySaving"
                  />
                  <span>{{ roleLabels[role] | t }}</span>
                </label>
              }
            </div>
            <div class="security-actions">
              <button
                type="button"
                class="security-action"
                [disabled]="isPolicySaving"
                (click)="savePolicy()"
              >
                {{ isPolicySaving ? ('security.policy.saving' | t) : ('security.policy.save' | t) }}
              </button>
              @if (policyScope === 'company' && policyOverrideEnabled) {
                <button
                  type="button"
                  class="security-action security-action--ghost"
                  [disabled]="isPolicySaving"
                  (click)="useInheritedPolicy()"
                >
                  {{ 'security.policy.useSystemDefaults' | t }}
                </button>
              }
            </div>
          </lib-data-view-shell>
        }
      </section>
    }
    </lib-page-shell>
  `,
  styles: [`
    .security-card {
      background: #ffffff;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 16px;
      display: grid;
      gap: 12px;
      max-width: 760px;
    }
    .security-meta {
      color: #4b5563;
      font-size: 14px;
    }
    .security-action {
      width: fit-content;
      height: 38px;
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #fff;
      padding: 0 14px;
      cursor: pointer;
    }
    .security-action--danger {
      background: #b91c1c;
    }
    .security-action--ghost {
      background: #374151;
    }
    .security-setup,
    .security-disable {
      display: grid;
      gap: 10px;
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 12px;
      background: #f9fafb;
    }
    .security-setup h4,
    .security-recovery h4 {
      margin: 0;
    }
    .security-qr {
      width: 220px;
      height: 220px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #fff;
    }
    .security-secret,
    .security-uri {
      color: #374151;
      font-size: 13px;
      word-break: break-all;
    }
    .security-setup form,
    .security-disable {
      display: grid;
      gap: 8px;
    }
    .security-setup label,
    .security-disable label {
      display: flex;
      flex-direction: column;
      gap: 6px;
      font-size: 13px;
      color: #374151;
    }
    .security-setup input,
    .security-disable input {
      height: 38px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 10px;
      box-sizing: border-box;
    }
    .security-recovery {
      border: 1px solid #fde68a;
      background: #fffbeb;
      border-radius: 10px;
      padding: 12px;
    }
    .security-recovery ul {
      margin: 8px 0 0;
      padding-left: 18px;
    }
    .security-roles-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 8px 12px;
    }
    .security-role-toggle {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
      color: #1f2937;
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
    }
    .security-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
  `],
})
export class SecurityPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected readonly enableForm = this.formBuilder.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(32)]],
  });

  protected readonly disableForm = this.formBuilder.group({
    code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(32)]],
  });

  protected isLoadingStatus = true;
  protected isBusy = false;
  protected twoFactorRequired = false;
  protected twoFactorEnabled = false;
  protected actionErrorText: string | null = null;
  protected successText: string | null = null;
  protected recoveryCodes: string[] = [];
  protected setupData: { secret: string; otpauthUrl: string; qrImageUrl: string } | null = null;
  protected isPolicyLoading = false;
  protected isPolicySaving = false;
  protected policyErrorText: string | null = null;
  protected policySuccessText: string | null = null;
  protected editableMandatoryRoles: string[] = [];
  protected mandatoryRoles = new Set<string>();
  protected policyOverrideEnabled = false;
  protected policyScope: 'global' | 'company' = 'company';
  protected canManagePolicy = false;
  protected policySectionTitle = 'security.policy.title';
  protected policySectionDescription = 'security.policy.description';
  protected roleLabels: Record<string, string> = {
    manager: 'role.manager',
    technician: 'role.technician',
    specialist: 'role.specialist',
    reception: 'role.reception',
    client: 'role.client',
    company_admin: 'role.company_admin',
  };
  protected contextCompanyId = '';
  protected isGlobalScope = false;

  ngOnInit(): void {
    const role = this.authService.getCurrentRole();
    this.canManagePolicy = role === 'company_admin' || role === 'system_admin';
    this.policyScope = role === 'system_admin' ? 'global' : 'company';
    this.policySectionTitle = role === 'system_admin'
      ? 'security.policy.title.system'
      : 'security.policy.title.company';
    this.policySectionDescription = role === 'system_admin'
      ? 'security.policy.description.system'
      : 'security.policy.description.company';

    this.loadStatus();
    if (this.canManagePolicy) {
      if (role === 'system_admin') {
        this.contextCompanyId = this.systemAdminContext.getCompanyId() ?? '';
        this.isGlobalScope = this.systemAdminContext.isGlobalScope();
        this.subscriptions.add(
          combineLatest([
            this.systemAdminContext.companyId$,
            this.systemAdminContext.scopeMode$,
          ]).pipe(
            map(([companyId, scopeMode]) => {
              const normalizedCompanyId = companyId ?? '';
              const isGlobalScope = scopeMode === 'global';

              return {
                companyId: normalizedCompanyId,
                isGlobalScope,
                key: isGlobalScope ? 'global' : `company:${normalizedCompanyId}`,
              };
            }),
            distinctUntilChanged((previous, current) => previous.key === current.key),
          ).subscribe(({ companyId, isGlobalScope }) => {
            this.contextCompanyId = companyId;
            this.isGlobalScope = isGlobalScope;
            this.loadPolicy();
          }),
        );
        return;
      }

      this.loadPolicy();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected startSetup(): void {
    this.isBusy = true;
    this.actionErrorText = null;
    this.successText = null;
    this.recoveryCodes = [];

    this.authService.setupTwoFactor()
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: (data) => {
          this.setupData = data;
          this.enableForm.reset({ code: '' });
        },
        error: () => {
          this.actionErrorText = 'security.error.setupInit';
        },
      });
  }

  protected confirmEnable(): void {
    if (this.enableForm.invalid) {
      return;
    }

    const code = this.enableForm.controls.code.value?.trim() ?? '';
    this.isBusy = true;
    this.actionErrorText = null;
    this.successText = null;

    this.authService.enableTwoFactor(code)
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: (response) => {
          this.twoFactorEnabled = true;
          this.setupData = null;
          this.recoveryCodes = response.recoveryCodes;
          this.successText = 'security.success.enabled';
          this.loadStatus();
        },
        error: () => {
          this.actionErrorText = 'security.error.invalidCode';
        },
      });
  }

  protected disableTwoFactor(): void {
    if (this.disableForm.invalid) {
      return;
    }

    const code = this.disableForm.controls.code.value?.trim() ?? '';
    this.isBusy = true;
    this.actionErrorText = null;
    this.successText = null;

    this.authService.disableTwoFactor(code)
      .pipe(finalize(() => (this.isBusy = false)))
      .subscribe({
        next: () => {
          this.setupData = null;
          this.recoveryCodes = [];
          this.successText = 'security.success.disabled';
          this.loadStatus();
        },
        error: () => {
          this.actionErrorText = 'security.error.disable';
        },
      });
  }

  private loadStatus(): void {
    this.isLoadingStatus = true;
    this.authService.getTwoFactorStatus()
      .pipe(finalize(() => (this.isLoadingStatus = false)))
      .subscribe({
        next: (status) => {
          this.twoFactorRequired = status.required;
          this.twoFactorEnabled = status.enabled;
        },
        error: () => {
          this.actionErrorText = 'security.error.loadStatus';
        },
      });
  }

  protected isRoleMandatory(role: string): boolean {
    return this.mandatoryRoles.has(role);
  }

  protected toggleMandatoryRole(role: string, enabled: boolean): void {
    if (enabled) {
      this.mandatoryRoles.add(role);
      return;
    }
    this.mandatoryRoles.delete(role);
  }

  protected savePolicy(): void {
    this.policyErrorText = null;
    this.policySuccessText = null;
    this.isPolicySaving = true;
    const roles = this.editableMandatoryRoles.filter((role) => this.mandatoryRoles.has(role));

    this.authService.updateTwoFactorPolicy(roles, false, this.getPolicyTargetCompanyId())
      .pipe(finalize(() => (this.isPolicySaving = false)))
      .subscribe({
        next: (policy) => {
          this.editableMandatoryRoles = [...policy.editableRoles];
          this.mandatoryRoles = new Set(policy.mandatoryRoles);
          this.policyOverrideEnabled = policy.overrideEnabled;
          this.policyScope = policy.scope;
          this.policySuccessText = 'security.policy.success.updated';
        },
        error: () => {
          this.policyErrorText = 'security.policy.error.update';
        },
      });
  }

  private loadPolicy(): void {
    if (this.shouldBlockCompanyScopePolicy()) {
      this.editableMandatoryRoles = [];
      this.mandatoryRoles = new Set<string>();
      this.policyOverrideEnabled = false;
      this.policyErrorText = null;
      this.policySuccessText = null;
      this.isPolicyLoading = false;
      return;
    }

    this.isPolicyLoading = true;
    this.policyErrorText = null;
    this.policySuccessText = null;

    this.authService.getTwoFactorPolicy(this.getPolicyTargetCompanyId())
      .pipe(finalize(() => (this.isPolicyLoading = false)))
      .subscribe({
        next: (policy) => {
          this.editableMandatoryRoles = [...policy.editableRoles];
          this.mandatoryRoles = new Set(policy.mandatoryRoles);
          this.policyOverrideEnabled = policy.overrideEnabled;
          this.policyScope = policy.scope;
        },
        error: () => {
          this.policyErrorText = 'security.policy.error.load';
        },
      });
  }

  protected useInheritedPolicy(): void {
    if (this.policyScope !== 'company') {
      return;
    }

    this.policyErrorText = null;
    this.policySuccessText = null;
    this.isPolicySaving = true;
    this.authService.updateTwoFactorPolicy([], true, this.getPolicyTargetCompanyId())
      .pipe(finalize(() => (this.isPolicySaving = false)))
      .subscribe({
        next: (policy) => {
          this.editableMandatoryRoles = [...policy.editableRoles];
          this.mandatoryRoles = new Set(policy.mandatoryRoles);
          this.policyOverrideEnabled = policy.overrideEnabled;
          this.policyScope = policy.scope;
          this.policySuccessText = 'security.policy.success.inherited';
        },
        error: () => {
          this.policyErrorText = 'security.policy.error.inherited';
        },
      });
  }

  protected get isSystemAdminActor(): boolean {
    return this.authService.getCurrentRole() === 'system_admin';
  }

  protected get isCompanyScopeWithoutCompany(): boolean {
    return this.isSystemAdminActor
      && !this.isGlobalScope
      && !this.contextCompanyId.trim();
  }

  private getPolicyTargetCompanyId(): string | undefined {
    if (!this.isSystemAdminActor) {
      return undefined;
    }

    if (this.isGlobalScope) {
      return undefined;
    }

    return this.contextCompanyId.trim() || undefined;
  }

  private shouldBlockCompanyScopePolicy(): boolean {
    return this.isSystemAdminActor
      && !this.isGlobalScope
      && !this.contextCompanyId.trim();
  }
}
